import {
  CreditApplication,
  CreditApplicationFieldsValues,
  domainsMatch,
  extractDomain,
  getProvidedCompanyWebsite,
  getDomainAgeInDays,
  getVerifiedWebsiteFromMiddeskReport,
  isFreeEmailDomain,
  NEW_DOMAIN_THRESHOLD_DAYS,
} from '@dill/dill-shared';
import { useMemo } from 'react';
import {
  Buildings,
  CalendarBlank,
  CheckCircle,
  Clock,
  EnvelopeSimple,
  Fingerprint,
  Globe,
  House,
  IdentificationBadge,
  IdentificationCard,
  LinkSimple,
  MapPin,
  Receipt,
  Scales,
  ShieldCheck,
  Warning,
} from 'phosphor-react';
import {
  getBureauScores,
  getIpLocationFlag,
  getMiddeskSummary,
  getMiddeskTestSummary,
} from './creditApplicationSummaryUtils';

type Tone = 'success' | 'warning' | 'error' | 'neutral';

type CheckRowData = {
  label: string;
  detail: string;
  tone: Tone;
  icon: JSX.Element;
  // Application fields this check is based on; clicking the check scrolls to the first one found.
  targetFieldIds?: string[];
};

// 'ein-number' matches EIN fields by substring, like the backend's duplicate EIN check.
const EIN_FIELD_IDS = ['company-ein', 'ein-number'];
const CHECK_TARGET_FIELD_IDS: Record<string, string[]> = {
  'Duplicate EIN': EIN_FIELD_IDS,
  'Email Domain Match': ['company-website', 'email-personal'],
  'Email Verification': ['email-personal'],
  'Applicant Location': ['company-state'],
  'Business Name Match': ['customer-or-company-name'],
  'Secretary of State Match': ['customer-or-company-name'],
  'EIN / Tax ID Match': EIN_FIELD_IDS,
  'Website Domain Check': ['company-website'],
  'Tax Exempt Certificate Authentication': ['tax-exempt-choice'],
};

// Fraud/duplicate checks only run once an application has been submitted —
// before that there's simply no data yet, which is a distinct state from "passed".
const PRE_SUBMISSION_STATUSES = ['DRAFT', 'OPENED', 'SENT'];

const TONE_STYLES: Record<Tone, { pill: string; avatar: string; icon: JSX.Element }> = {
  success: {
    pill: 'SUCCESS_100-BG SUCCESS_500-CLR SUCCESS_500-BORDER',
    avatar: 'SUCCESS_100-BG SUCCESS_500-CLR',
    icon: <CheckCircle size={12} weight="bold" />,
  },
  warning: {
    pill: 'WARNING_100-BG WARNING_500-CLR WARNING_500-BORDER',
    avatar: 'WARNING_100-BG WARNING_500-CLR',
    icon: <Warning size={12} weight="bold" />,
  },
  error: {
    pill: 'ERROR_100-BG ERROR_500-CLR ERROR_500-BORDER',
    avatar: 'ERROR_100-BG ERROR_500-CLR',
    icon: <Warning size={12} weight="bold" />,
  },
  neutral: {
    pill: 'bg-gray-100 text-gray-500 border-gray-200',
    avatar: 'bg-gray-100 text-gray-400',
    icon: <Clock size={12} weight="bold" />,
  },
};

const StatusPill = ({ tone, label }: { tone: Tone; label: string }) => {
  const { pill, icon } = TONE_STYLES[tone];
  return (
    <span
      className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${pill}`}>
      {icon}
      {label}
    </span>
  );
};

const STATUS_LABEL: Record<Tone, string> = {
  success: 'Passed',
  warning: 'Flagged',
  error: 'Failed',
  neutral: 'Pending',
};

const CheckRow = ({ row, onClick }: { row: CheckRowData; onClick?: () => void }) => (
  <div
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={(e) => {
      if (onClick && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onClick();
      }
    }}
    title={onClick ? 'Go to the related field' : undefined}
    className={`flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50/60 px-2 py-1 ${
      onClick ? 'cursor-pointer hover:border-gray-300 hover:bg-gray-100/70 transition-colors' : ''
    }`}>
    <div
      className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
        TONE_STYLES[row.tone].avatar
      }`}>
      {row.icon}
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center justify-between gap-1 w-full py-1">
        <p className="text-xs font-semibold text-gray-700">{row.label}</p>
        <StatusPill tone={row.tone} label={STATUS_LABEL[row.tone]} />
      </div>
      <p className="text-[11px] GREY_500-CLR leading-snug">{row.detail}</p>
    </div>
  </div>
);

const addressFieldLabel = (fieldId: string) =>
  fieldId.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const middeskTaskTone = (status?: string): Tone => {
  const normalized = (status ?? '').toLowerCase();
  if (normalized.includes('success')) return 'success';
  if (normalized.includes('warning')) return 'warning';
  return 'error';
};

const ICON_SIZE = 12;
const CHECK_ICONS = {
  duplicateEin: <Fingerprint size={ICON_SIZE} weight="bold" />,
  emailDomain: <EnvelopeSimple size={ICON_SIZE} weight="bold" />,
  emailVerification: <LinkSimple size={ICON_SIZE} weight="bold" />,
  location: <MapPin size={ICON_SIZE} weight="bold" />,
  businessName: <Buildings size={ICON_SIZE} weight="bold" />,
  sos: <Scales size={ICON_SIZE} weight="bold" />,
  ein: <IdentificationCard size={ICON_SIZE} weight="bold" />,
  address: <House size={ICON_SIZE} weight="bold" />,
  taxExempt: <Receipt size={ICON_SIZE} weight="bold" />,
  principalIdentity: <IdentificationBadge size={ICON_SIZE} weight="bold" />,
  websiteDomain: <Globe size={ICON_SIZE} weight="bold" />,
  domainAge: <CalendarBlank size={ICON_SIZE} weight="bold" />,
};

const isAffirmative = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return /^(yes|true)$/i.test(value.trim());
  return false;
};

type Props = {
  creditApplication?: CreditApplication | null;
  formData?: CreditApplicationFieldsValues[];
  pulledCreditReports?: any[] | null;
  onViewFullReport?: () => void;
  // Called with a check's related field ids when it's clicked.
  onSelectCheckFields?: (fieldIds: string[]) => void;
};

const CreditApplicationSummaryStep = ({
  creditApplication,
  formData,
  pulledCreditReports,
  onViewFullReport,
  onSelectCheckFields,
}: Props) => {
  const companyStateAbbr = formData?.find((field) => field.id === 'company-state')?.value ?? null;

  const bureauScores = useMemo(() => getBureauScores(pulledCreditReports), [pulledCreditReports]);
  const hasAnyScore = Boolean(
    bureauScores.creditSafe || bureauScores.experian || bureauScores.nacm
  );

  const middeskSummary = useMemo(() => {
    const report = creditApplication?.middeskReport?.report;
    if (report) return getMiddeskSummary(report);
    // Outside production there's no real report to summarize yet — fall back
    // to the same sample data derived from the applicant's own form fields
    // that PreviewCreditApplicationData shows inline, so these checks stay
    // testable without a live Middesk pull.
    if (process.env.REACT_APP_ENVIRONMENT !== 'production') {
      return getMiddeskTestSummary(formData ?? []);
    }
    return null;
  }, [creditApplication?.middeskReport?.report, formData]);

  const verifiedWebsite = useMemo(
    () => getVerifiedWebsiteFromMiddeskReport(creditApplication?.middeskReport?.report),
    [creditApplication?.middeskReport?.report]
  );

  const isPreSubmission = PRE_SUBMISSION_STATUSES.includes(creditApplication?.status ?? '');

  const fraudChecks = useMemo((): CheckRowData[] => {
    const rows: CheckRowData[] = [];

    // Duplicate EIN
    if (isPreSubmission) {
      rows.push({
        label: 'Duplicate EIN',
        detail: 'Checked once the application is submitted.',
        tone: 'neutral',
        icon: CHECK_ICONS.duplicateEin,
      });
    } else {
      rows.push(
        creditApplication?.isDuplicate
          ? {
              label: 'Duplicate EIN',
              detail: 'A credit application with this EIN already exists for this supplier.',
              tone: 'warning',
              icon: CHECK_ICONS.duplicateEin,
            }
          : {
              label: 'Duplicate EIN',
              detail: 'No other credit application uses this EIN.',
              tone: 'success',
              icon: CHECK_ICONS.duplicateEin,
            }
      );
    }

    // Website / email domain checks. The verified website is discovered by Middesk from the
    // company name and address — never taken from what the applicant typed in.
    const emailDomain = extractDomain(creditApplication?.senderEmail);
    const providedDomain = extractDomain(getProvidedCompanyWebsite(formData));
    const verifiedDomain =
      verifiedWebsite.state === 'found' ? verifiedWebsite.website.domain : null;
    const pendingDetail = isPreSubmission
      ? 'Checked once the application is submitted.'
      : 'Waiting on the verified website lookup to complete.';

    // When the template asks for a website, the applicant's answer is shown and checked in this
    // same card. It's never trusted on its own — a lookalike domain would match the applicant's
    // email — so it's only ever compared against the one Middesk discovered.
    if (verifiedWebsite.state === 'found') {
      const providedMatches = providedDomain ? domainsMatch(providedDomain, verifiedDomain) : true;
      const providedNote = providedDomain
        ? ` Applicant provided ${providedDomain} — ${
            providedMatches ? 'matches' : 'does not match'
          }.`
        : '';
      rows.push({
        label: 'Verified Website',
        detail: `${verifiedWebsite.website.domain} — found from the company name and address.${providedNote}`,
        tone: providedMatches ? 'success' : 'warning',
        icon: CHECK_ICONS.websiteDomain,
      });
    } else if (verifiedWebsite.state === 'not_found') {
      rows.push({
        label: 'Verified Website',
        detail: `No website could be found for this company name and address.${
          providedDomain
            ? ` Applicant provided ${providedDomain}, which couldn't be confirmed.`
            : ''
        }`,
        tone: 'neutral',
        icon: CHECK_ICONS.websiteDomain,
      });
    } else {
      rows.push({
        label: 'Verified Website',
        detail: `${pendingDetail}${providedDomain ? ` Applicant provided ${providedDomain}.` : ''}`,
        tone: 'neutral',
        icon: CHECK_ICONS.websiteDomain,
      });
    }

    // Verified website domain age — straight from Middesk's WHOIS data on the discovered site
    // (website.domain.creation_date), so no separate lookup. A real business's site is usually
    // years old; a brand-new one is worth a second look.
    if (verifiedWebsite.state === 'found') {
      const { domain, registeredAt, registrar } = verifiedWebsite.website;
      const websiteAgeDays = getDomainAgeInDays(registeredAt);
      const registrarNote = registrar ? ` via ${registrar}` : '';
      if (websiteAgeDays === null) {
        rows.push({
          label: 'Website Domain Age',
          detail: `Middesk didn't return a registration date for ${domain}.`,
          tone: 'neutral',
          icon: CHECK_ICONS.domainAge,
        });
      } else {
        const registeredOn = new Date(registeredAt as string).toLocaleDateString();
        rows.push(
          websiteAgeDays < NEW_DOMAIN_THRESHOLD_DAYS
            ? {
                label: 'Website Domain Age',
                detail: `${domain} was registered only ${websiteAgeDays} day${
                  websiteAgeDays === 1 ? '' : 's'
                } ago (${registeredOn}${registrarNote}).`,
                tone: 'warning',
                icon: CHECK_ICONS.domainAge,
              }
            : {
                label: 'Website Domain Age',
                detail: `${domain} has been registered since ${registeredOn}${registrarNote}.`,
                tone: 'success',
                icon: CHECK_ICONS.domainAge,
              }
        );
      }
    }

    if (!emailDomain) {
      rows.push({
        label: 'Email vs Verified Website',
        detail: 'No applicant email captured for this application.',
        tone: 'neutral',
        icon: CHECK_ICONS.emailDomain,
      });
    } else if (!verifiedDomain) {
      rows.push({
        label: 'Email vs Verified Website',
        detail:
          verifiedWebsite.state === 'not_found'
            ? `Couldn't compare ${emailDomain} — no verified website was found.`
            : pendingDetail,
        tone: 'neutral',
        icon: CHECK_ICONS.emailDomain,
      });
    } else {
      rows.push(
        domainsMatch(emailDomain, verifiedDomain)
          ? {
              label: 'Email vs Verified Website',
              detail: `Applicant email domain (${emailDomain}) matches the verified website (${verifiedDomain}).`,
              tone: 'success',
              icon: CHECK_ICONS.emailDomain,
            }
          : {
              label: 'Email vs Verified Website',
              detail: `Applicant email domain (${emailDomain}) does not match the verified website (${verifiedDomain}).`,
              tone: 'warning',
              icon: CHECK_ICONS.emailDomain,
            }
      );
    }

    // Business email — a personal mailbox (gmail, outlook, yahoo...) can't be tied to the company.
    if (emailDomain) {
      rows.push(
        isFreeEmailDomain(emailDomain)
          ? {
              label: 'Business Email',
              detail: `Applicant used a personal email provider (${emailDomain}) instead of a company email domain.`,
              tone: 'warning',
              icon: CHECK_ICONS.emailDomain,
            }
          : {
              label: 'Business Email',
              detail: `Applicant used a company email domain (${emailDomain}).`,
              tone: 'success',
              icon: CHECK_ICONS.emailDomain,
            }
      );
    }

    // Only when the template asks for a website (provided vs verified is in the card above).
    if (providedDomain) {
      if (isPreSubmission || !emailDomain) {
        rows.push({
          label: 'Email vs Provided Website',
          detail: isPreSubmission
            ? pendingDetail
            : 'No applicant email captured for this application.',
          tone: 'neutral',
          icon: CHECK_ICONS.emailDomain,
        });
      } else {
        rows.push(
          domainsMatch(emailDomain, providedDomain)
            ? {
                label: 'Email vs Provided Website',
                detail: `Applicant email domain (${emailDomain}) matches the provided website.`,
                tone: 'success',
                icon: CHECK_ICONS.emailDomain,
              }
            : {
                label: 'Email vs Provided Website',
                detail: `Applicant email domain (${emailDomain}) does not match the provided website (${providedDomain}).`,
                tone: 'warning',
                icon: CHECK_ICONS.emailDomain,
              }
        );
      }
    }

    // Email verification (applicant signed in through a secure emailed link before submitting)
    rows.push(
      creditApplication?.metaData?.ipAddress
        ? {
            label: 'Email Verification',
            detail:
              'Applicant verified their email through an email verification link before they could submit this application',
            tone: 'success',
            icon: CHECK_ICONS.emailVerification,
          }
        : {
            label: 'Email Verification',
            detail: 'No email ownership verification was captured for this application.',
            tone: 'neutral',
            icon: CHECK_ICONS.emailVerification,
          }
    );

    // Applicant IP / location
    const location = creditApplication?.metaData?.location;
    if (!location) {
      rows.push({
        label: 'Applicant Location',
        detail: 'No IP-based location was captured for this application.',
        tone: 'neutral',
        icon: CHECK_ICONS.location,
      });
    } else {
      const flagReason = getIpLocationFlag(location, companyStateAbbr);
      rows.push(
        flagReason
          ? {
              label: 'Applicant Location',
              detail: flagReason,
              tone: 'warning',
              icon: CHECK_ICONS.location,
            }
          : {
              label: 'Applicant Location',
              detail: `Applied from ${location}, matching the company's state.`,
              tone: 'success',
              icon: CHECK_ICONS.location,
            }
      );
    }

    // Business name match (Middesk)
    rows.push(
      middeskSummary?.nameMatch
        ? {
            label: 'Business Name Match',
            detail: middeskSummary.nameMatch.message || middeskSummary.nameMatch.label,
            tone: middeskTaskTone(middeskSummary.nameMatch.status),
            icon: CHECK_ICONS.businessName,
          }
        : {
            label: 'Business Name Match',
            detail: 'Not yet verified — business verification hasn’t been run.',
            tone: 'neutral',
            icon: CHECK_ICONS.businessName,
          }
    );

    // Secretary of State match (Middesk)
    rows.push(
      middeskSummary?.sosMatch
        ? {
            label: 'Secretary of State Match',
            detail: middeskSummary.sosMatch.message || middeskSummary.sosMatch.label,
            tone: middeskTaskTone(middeskSummary.sosMatch.status),
            icon: CHECK_ICONS.sos,
          }
        : {
            label: 'Secretary of State Match',
            detail: 'Not yet verified — no SOS check has been run for this application.',
            tone: 'neutral',
            icon: CHECK_ICONS.sos,
          }
    );

    // EIN / Tax ID match (Middesk)
    rows.push(
      middeskSummary?.tinMatch
        ? {
            label: 'EIN / Tax ID Match',
            detail: middeskSummary.tinMatch.message || middeskSummary.tinMatch.label,
            tone: middeskTaskTone(middeskSummary.tinMatch.status),
            icon: CHECK_ICONS.ein,
          }
        : {
            label: 'EIN / Tax ID Match',
            detail: 'Not yet verified — no EIN check has been run for this application.',
            tone: 'neutral',
            icon: CHECK_ICONS.ein,
          }
    );

    // Address verification (shipping/mailing address, same fields verified elsewhere in the app)
    const verifiedAddressFields = (formData ?? []).filter(
      (field) => (field.id || '').endsWith('-address') && field.addressVerification?.message
    );
    if (verifiedAddressFields.length === 0) {
      rows.push({
        label: 'Address Verification',
        detail: 'No address verification has been run for this application.',
        tone: 'neutral',
        icon: CHECK_ICONS.address,
      });
    } else {
      verifiedAddressFields.forEach((field) => {
        const verification = field.addressVerification;
        if (!verification) return;
        rows.push({
          label: `${addressFieldLabel(field.id)} Verification`,
          targetFieldIds: [field.id],
          detail:
            verification.message +
            (verification.standardizedAddress ? ` (${verification.standardizedAddress})` : ''),
          tone: verification.verified ? 'success' : 'warning',
          icon: CHECK_ICONS.address,
        });
      });
    }

    // Principal identity verification — not yet wired to a real data source, dev/staging only so
    // it doesn't show as always-passing in production.
    if (process.env.REACT_APP_ENVIRONMENT !== 'production') {
      rows.push({
        label: 'Principal Identity Verification',
        detail: 'Verifies the identity of business owners and key contacts.',
        tone: 'neutral',
        icon: CHECK_ICONS.principalIdentity,
      });
    }

    // Tax exempt certificate authentication — not yet ready for production, dev/staging only.
    if (process.env.REACT_APP_ENVIRONMENT !== 'production') {
      const taxExemptChoice = (formData ?? []).find((field) => field.id === 'tax-exempt-choice');
      const taxExemptNumber = (formData ?? []).find((field) => field.id === 'tax-exempt-number');
      const taxExemptFile = (formData ?? []).find((field) => field.id === 'tax-exempt-file');
      const claimsTaxExempt = isAffirmative(taxExemptChoice?.value);
      const taxExemptFileUrl = (taxExemptFile?.value as { url?: string } | null)?.url;

      if (!claimsTaxExempt) {
        rows.push({
          label: 'Tax Exempt Certificate Authentication',
          detail: 'Applicant did not claim tax-exempt status.',
          tone: 'neutral',
          icon: CHECK_ICONS.taxExempt,
        });
      } else if (taxExemptFileUrl) {
        rows.push({
          label: 'Tax Exempt Certificate Authentication',
          detail: taxExemptNumber?.value
            ? `Certificate #${taxExemptNumber.value} provided and on file.`
            : 'Tax-exempt certificate provided and on file.',
          tone: 'success',
          icon: CHECK_ICONS.taxExempt,
        });
      } else {
        rows.push({
          label: 'Tax Exempt Certificate Authentication',
          detail: 'Applicant claims tax-exempt status, but no certificate was provided.',
          tone: 'warning',
          icon: CHECK_ICONS.taxExempt,
        });
      }
    }

    return rows.map((row) => ({
      ...row,
      targetFieldIds: row.targetFieldIds ?? CHECK_TARGET_FIELD_IDS[row.label],
    }));
  }, [
    creditApplication?.isDuplicate,
    creditApplication?.senderEmail,
    creditApplication?.metaData,
    verifiedWebsite,
    isPreSubmission,
    companyStateAbbr,
    middeskSummary,
    formData,
  ]);

  const overallBadge = useMemo(() => {
    const flagged = fraudChecks.filter((row) => row.tone === 'warning' || row.tone === 'error');
    const pending = fraudChecks.filter((row) => row.tone === 'neutral');
    if (flagged.length > 0) {
      return {
        tone: 'error' as Tone,
        label: `${flagged.length} issue${flagged.length > 1 ? 's' : ''} found`,
      };
    }
    if (pending.length === fraudChecks.length) {
      return { tone: 'neutral' as Tone, label: 'Verification pending' };
    }
    return { tone: 'success' as Tone, label: 'All checks passed' };
  }, [fraudChecks]);

  return (
    <div className="mt-4 mb-3">
      <div className="w-full rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm">
        {/* Card header */}
        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <ShieldCheck size={14} weight="bold" className="GREY_500-CLR" />
            <h2 className="text-sm font-bold text-gray-800 tracking-wide">Summary</h2>
          </div>
          <StatusPill tone={overallBadge.tone} label={overallBadge.label} />
        </div>

        {/* Credit Score */}
        <div className="flex items-center justify-between px-4 pt-1.5 pb-0.5">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            Credit Score
          </span>
          {hasAnyScore && onViewFullReport && (
            <button
              type="button"
              onClick={onViewFullReport}
              className="text-[11px] font-semibold PRIMARY_500-CLR hover:underline">
              View full reports &rarr;
            </button>
          )}
        </div>
        {hasAnyScore ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 px-4 pb-1.5 pt-1">
            {bureauScores.creditSafe && (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-1.5">
                <span className="text-[10px] font-semibold GREY_500-CLR uppercase tracking-wide">
                  CreditSafe
                </span>
                <span className="text-sm font-bold text-gray-900 whitespace-nowrap">
                  {bureauScores.creditSafe.score}
                  <span className="text-[10px] font-normal GREY_500-CLR ml-1.5">
                    Limit: {bureauScores.creditSafe.creditLimit}
                  </span>
                </span>
              </div>
            )}
            {bureauScores.experian && (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-1.5">
                <span className="text-[10px] font-semibold GREY_500-CLR uppercase tracking-wide">
                  Experian
                </span>
                <span className="text-sm font-bold text-gray-900 whitespace-nowrap">
                  {bureauScores.experian.intelliscore ?? bureauScores.experian.financialScoreRisk}
                  {bureauScores.experian.creditLimit && (
                    <span className="text-[10px] font-normal GREY_500-CLR ml-1.5">
                      Limit: {bureauScores.experian.creditLimit}
                    </span>
                  )}
                </span>
              </div>
            )}
            {bureauScores.nacm && (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-1.5">
                <span className="text-[10px] font-semibold GREY_500-CLR uppercase tracking-wide">
                  NACM
                </span>
                <span className="text-sm font-bold text-gray-900">{bureauScores.nacm.score}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="px-4 pb-1.5 pt-1 text-xs GREY_500-CLR">No credit reports pulled yet.</div>
        )}

        {/* Fraud Prevention Summary */}
        <div className="px-4 pt-2 pb-0.5 border-t border-gray-100">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            Fraud Prevention Summary
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 px-4 pb-3 pt-1">
          {fraudChecks.map((row, index) => (
            <CheckRow
              key={`${row.label}-${index}`}
              row={row}
              onClick={
                onSelectCheckFields && row.targetFieldIds?.length
                  ? () => onSelectCheckFields(row.targetFieldIds ?? [])
                  : undefined
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CreditApplicationSummaryStep;
